import { BookingFeeType, FeeType } from '../constants/pricing'
import { BookingPaymentType } from '../constants/booking'
import { InventoryType } from '../constants/inventory'
import type { BookingFee } from '../booking'
import type { InventoryTier, InventoryTierLevel } from '../inventory'
import type { Venue } from '../venue'

const paymentTypesWithProcessingFees = [
  BookingPaymentType.ADVANCE,
  BookingPaymentType.ADVANCE_DEPOSIT,
]

export type BuyerTotal = {
  total: number
  dueOnArrival: number
  price: number
  taxAndTip: number
  venueFeeTotal: number
  processingFeeTotal: number
  userPaysProcessing?: boolean
  paymentType: BookingPaymentType
}

type BuyerTotalVenue = Pick<
  Partial<Venue>,
  | 'paymentProviderAccountId'
  | 'venueTicketProcessingFee'
  | 'ticketProcessingFee'
  | 'venueTableProcessingFee'
  | 'tableProcessingFee'
>

export type BuyerTotalOptions = {
  paymentType: BookingPaymentType
  inventoryType: InventoryType
  price: number
  depositAmount?: number
  taxPercent: number
  tipPercent: number
  fees: BookingFee[]
  tier?: InventoryTier
  userPaysProcessing?: boolean
  venue?: BuyerTotalVenue
}

export type BuyerTotalResult = {
  minimum: number
  price: number
  depositAmount: number
  subtotal: number
  tax: number
  taxPercent: number
  tip: number
  tipPercent: number
  taxAndTip: number
  total: number
  dueOnArrival: number
  paymentType: BookingPaymentType
  venueFeeTotal: number
  processingFeeTotal: number
  processingFeePercent: number
  userPaysProcessing?: boolean
  fees: BookingFee[]
  feeTotal: number
  processingFees: BookingFee[]
  venueAbsorbedProcessingFeeTotal: number
  userPaidProcessingFeeTotal: number
}

export function numberToCurrency(calculated: number): number {
  if (Number.isNaN(calculated) || calculated === 0) {
    return 0
  }

  return Number(calculated.toFixed(2))
}

export function calculateFeeAmount(val: number, fee: BookingFee): number {
  if (!fee || !fee.amount) return 0

  const feeAmount = Number(fee.amount || 0)
  val = val || 0

  switch (fee.type) {
    case FeeType.FLAT:
      return numberToCurrency(feeAmount)
    case FeeType.PERCENT:
      if (!val) return 0
      return numberToCurrency(val * feeAmount)
    default:
      return 0
  }
}

export function isFeeTaxable(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return false
}

export function normalizeFees(fees: BookingFee[] = []): BookingFee[] {
  if (!Array.isArray(fees)) return []

  return fees.map((fee) => ({
    ...fee,
    amount: Number(fee?.amount || 0),
    feeType: fee?.feeType || BookingFeeType.OPTIONAL,
    taxable: isFeeTaxable(fee?.taxable),
  }))
}

export function calculateFeeBreakdown(
  price = 0,
  fees: BookingFee[] = [],
  purchaseQuantity = 1
): {
  fees: BookingFee[]
  feeTotal: number
  taxableFeeTotal: number
} {
  let feeTotal = 0
  let taxableFeeTotal = 0
  const newFees: BookingFee[] = []

  fees.forEach((fee) => {
    if (!isFeeTaxable(fee.taxable)) return

    let taxableFeeAdd = calculateFeeAmount(price, fee)
    taxableFeeAdd *= purchaseQuantity

    if (taxableFeeAdd) {
      taxableFeeTotal += taxableFeeAdd
    }
  })

  fees.forEach((fee) => {
    let feeAdd = calculateFeeAmount(price, fee)
    feeAdd *= purchaseQuantity

    if (feeAdd) {
      feeTotal += feeAdd

      newFees.push({
        name: fee.name,
        amount: fee.amount,
        type: fee.type,
        feeType: fee.feeType,
        value: feeAdd,
        taxable: fee.taxable,
      })
    }
  })

  return {
    fees: newFees,
    feeTotal,
    taxableFeeTotal,
  }
}

export function calculateVenueFeeTotal(fees: BookingFee[]): number {
  if (!fees || fees.length === 0) return 0

  const nonProcessingFees = fees.filter(
    (fee) => fee?.feeType !== BookingFeeType.PROCESSING
  )

  const feeTotal = nonProcessingFees.reduce((sum, fee) => {
    return sum + (fee.value || 0)
  }, 0)

  return numberToCurrency(feeTotal)
}

export function getProcessingFee(
  preProcessingTotal: number,
  processingFeePercent: number
): BookingFee {
  preProcessingTotal = preProcessingTotal || 0
  processingFeePercent = processingFeePercent || 0

  const feeValue = preProcessingTotal * processingFeePercent

  return {
    name: 'Processing Fee',
    amount: processingFeePercent,
    type: FeeType.PERCENT,
    value: numberToCurrency(feeValue),
    feeType: BookingFeeType.PROCESSING,
  }
}

export function calculateBuyerTotal(
  options: BuyerTotalOptions
): BuyerTotalResult {
  const venue = options.venue

  if (!venue) {
    return {
      minimum: 0,
      price: 0,
      depositAmount: 0,
      subtotal: 0,
      tax: 0,
      taxPercent: 0,
      tip: 0,
      tipPercent: 0,
      taxAndTip: 0,
      total: 0,
      dueOnArrival: 0,
      paymentType: BookingPaymentType.NONE,
      venueFeeTotal: 0,
      processingFeeTotal: 0,
      processingFeePercent: 0,
      userPaysProcessing: true,
      fees: [],
      feeTotal: 0,
      processingFees: [],
      venueAbsorbedProcessingFeeTotal: 0,
      userPaidProcessingFeeTotal: 0,
    }
  }

  const quantity = 1
  let price = Number(options.price || 0)
  let taxPercent = Number(options.taxPercent || 0)
  let tipPercent = Number(options.tipPercent || 0)
  const normalizedFees = normalizeFees(options.fees)

  if (options.tier?.levels?.length) {
    let activeLevel: InventoryTierLevel | undefined = options.tier.levels.find(
      (level: InventoryTierLevel) => level._id === options.tier?.activeLevel
    )

    if (!activeLevel) {
      activeLevel = options.tier.levels[0] as InventoryTierLevel
    }

    if (activeLevel) {
      price = activeLevel.price
    }
  }

  let processingFeePercent = 0

  if (paymentTypesWithProcessingFees.includes(options.paymentType)) {
    if (options.inventoryType === InventoryType.TICKET) {
      processingFeePercent =
        (!!venue.paymentProviderAccountId
          ? venue.venueTicketProcessingFee
          : venue.ticketProcessingFee) ?? 0
    } else {
      processingFeePercent =
        (!!venue.paymentProviderAccountId
          ? venue.venueTableProcessingFee
          : venue.tableProcessingFee) ?? 0
    }
  }

  const calculatedFeeBreakdown = calculateFeeBreakdown(
    price,
    normalizedFees,
    quantity
  )
  let totalFees = calculatedFeeBreakdown.fees
  let calculatedFeeTotal = calculatedFeeBreakdown.feeTotal
  const calculatedTaxableFeeTotal = calculatedFeeBreakdown.taxableFeeTotal

  let calculatedPrice = price * quantity
  let depositAmount = 0
  let dueOnArrival = 0
  const calculatedMinimum = price * quantity
  let taxAmount = (calculatedPrice + calculatedTaxableFeeTotal) * taxPercent
  let tipAmount = calculatedPrice * tipPercent

  let preProcessingTotal: number

  if (options.paymentType === BookingPaymentType.ADVANCE_DEPOSIT) {
    const originalPrice = calculatedPrice
    depositAmount = options.depositAmount || 0
    calculatedPrice = depositAmount * quantity

    const depositDiff = price - depositAmount

    const calculatedDepositFeeBreakdown = calculateFeeBreakdown(
      originalPrice,
      normalizedFees,
      quantity
    )
    const calculatedDepositFeeTotal = calculatedDepositFeeBreakdown.feeTotal

    preProcessingTotal = options.depositAmount || 0

    const depositTaxAmount =
      (originalPrice + calculatedTaxableFeeTotal) * taxPercent
    const depositTipAmount = originalPrice * tipPercent

    dueOnArrival =
      depositDiff +
      depositTaxAmount +
      depositTipAmount +
      calculatedDepositFeeTotal

    taxAmount = 0
    taxPercent = 0
    tipAmount = 0
    tipPercent = 0
    totalFees = []
    calculatedFeeTotal = 0
  } else {
    preProcessingTotal =
      calculatedPrice + taxAmount + tipAmount + calculatedFeeTotal
  }

  if (
    options.paymentType === BookingPaymentType.VENUE ||
    options.paymentType === BookingPaymentType.NONE
  ) {
    dueOnArrival = preProcessingTotal
  }

  let postProcessingTotal = preProcessingTotal

  let processingFee: BookingFee | undefined
  let processingFeeTotal = 0

  if (paymentTypesWithProcessingFees.includes(options.paymentType)) {
    processingFee = getProcessingFee(preProcessingTotal, processingFeePercent)

    if (options.userPaysProcessing) {
      totalFees.push(processingFee)

      postProcessingTotal = preProcessingTotal + (processingFee.value || 0)
      calculatedFeeTotal += processingFee.value || 0
    }

    processingFeeTotal = processingFee.value || 0
  }

  return {
    minimum: numberToCurrency(calculatedMinimum),
    price: numberToCurrency(calculatedPrice),
    depositAmount: numberToCurrency(depositAmount),
    subtotal: numberToCurrency(calculatedPrice),
    tax: numberToCurrency(taxAmount),
    taxPercent: numberToCurrency(taxPercent),
    tip: numberToCurrency(tipAmount),
    tipPercent: numberToCurrency(tipPercent),
    taxAndTip: numberToCurrency(taxAmount + tipAmount),
    fees: totalFees,
    feeTotal: numberToCurrency(calculatedFeeTotal),
    processingFees: processingFee ? [processingFee] : [],
    processingFeeTotal: numberToCurrency(processingFeeTotal || 0),
    processingFeePercent: processingFee?.amount ?? 0,
    venueFeeTotal: calculateVenueFeeTotal(totalFees) || 0,
    venueAbsorbedProcessingFeeTotal: numberToCurrency(
      !options.userPaysProcessing ? processingFeeTotal : 0
    ),
    userPaidProcessingFeeTotal: numberToCurrency(
      options.userPaysProcessing ? processingFeeTotal : 0
    ),
    userPaysProcessing: options.userPaysProcessing,
    total: numberToCurrency(postProcessingTotal),
    dueOnArrival: numberToCurrency(dueOnArrival),
    paymentType: options.paymentType,
  }
}

/** Stored tax, tip, and percent-fee rates are decimals (0.04 = 4%). */
export function formatStoredPercentLabel(
  decimal: number | null | undefined
): string | null {
  if (decimal == null) return null
  const value = Number(decimal)
  if (!Number.isFinite(value) || value < 0) return null
  return `${Number((value * 100).toFixed(4))}%`
}

export function formatFeeConfiguredRateLabel(
  fee: { type?: string | null; amount?: number | null },
  formatMoney: (value: number | null | undefined) => string
): string | null {
  const amount = Number(fee.amount)
  if (!Number.isFinite(amount) || amount < 0 || fee.amount == null) return null
  if (fee.type === FeeType.FLAT) return formatMoney(amount)
  if (fee.type === FeeType.PERCENT) return formatStoredPercentLabel(amount)
  return null
}

export function formatMoneyWithConfiguredRate(args: {
  calculated: number | null | undefined
  rateLabel: string | null
  formatMoney: (value: number | null | undefined) => string
}): string {
  const money = args.formatMoney(args.calculated)
  return args.rateLabel ? `${money} (${args.rateLabel})` : money
}

type ReceiptProcessingFee = {
  name?: string | null
  value?: number | null
  amount?: number | null
  type?: string | null
  feeType?: string | null
}

function hasConfiguredCharge(fee: ReceiptProcessingFee): boolean {
  return (fee.value ?? 0) > 0 || (fee.amount ?? 0) > 0
}

/**
 * User-paid processing can live on `processingFees` or inside `fees`.
 * `processingFees` and `processingFeeTotal` stay populated when the venue
 * absorbs the fee and that amount is not in `total`, so those sources are
 * shown only when `tablelistFeeTotal` is positive.
 */
export function getPrepaidReceiptProcessingFees(receipt: {
  fees?: ReceiptProcessingFee[] | null
  processingFees?: ReceiptProcessingFee[] | null
  processingFeeTotal?: number | null
  tablelistFeeTotal?: number | null
}): ReceiptProcessingFee[] {
  const customerPaidProcessing = (receipt.tablelistFeeTotal ?? 0) > 0

  if (customerPaidProcessing) {
    const fromProcessing = (receipt.processingFees ?? []).filter(
      hasConfiguredCharge
    )
    if (fromProcessing.length > 0) return fromProcessing
  }

  const fromFees = (receipt.fees ?? []).filter(
    (fee) =>
      fee.feeType === BookingFeeType.PROCESSING && hasConfiguredCharge(fee)
  )
  if (fromFees.length > 0) return fromFees

  if (customerPaidProcessing && (receipt.processingFeeTotal ?? 0) > 0) {
    return [{ name: 'Processing Fee', value: receipt.processingFeeTotal }]
  }

  return []
}
